/**
 * 在投广告渠道分析主组件
 * 对应板块: 板块 D — 在投广告渠道分析
 */
import React, { useState, useMemo } from 'react';
import { Row, Col } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import CardCommon from '@op/components/CardCommon';
import {
  adsChannelHeatScoreTips,
  adsChannelImpressionTips,
  adsActiveAdsTips,
} from '@op/components/BubbleCommon';
import ChannelTrendCard from './ChannelTrendCard';
import ChannelFilter from './ChannelFilter';
import IconHot from '@op/assets/svg/icon-hot.svg';
import type {
  IChannelAnalysisProps,
  TChartType,
  ISectionConfig,
  TChannelMetric,
  TYAxisFormat,
} from './types';
import IconComputerBlue from '@op/assets/svg/icon-computer-blue.svg';
import { getChartMetricLabelKey } from '../../utils/metricLabels';
import './index.scss';

const ChannelAnalysis: React.FC<IChannelAnalysisProps> = ({
  filter,
  gameInfo,
  channelOptions = [],
}) => {
  const { formatMessage } = useLanguage();

  // ==================== 图表配置 ====================
  const chartConfigs = useMemo<ISectionConfig[]>(
    () => [
      {
        key: 'active_ads',
        section: (
          <div className="dbo-flex-title-icon">
            <img src={IconComputerBlue} />
            {formatMessage('Ad Count')}
          </div>
        ),
        charts: [
          {
            title: formatMessage(getChartMetricLabelKey('Active Ads', filter)),
            metric: 'creative_count_stat' as TChannelMetric,
            yAxisFormat: 'integer' as TYAxisFormat,
            tipsMap: adsActiveAdsTips,
          },
          {
            title: formatMessage(getChartMetricLabelKey('Active Ads% by Channel', filter)),
            metric: 'creative_count_stat_by_channel_percent' as TChannelMetric,
            yAxisFormat: 'percent' as TYAxisFormat,
          },
        ],
      },
      {
        key: 'heat_score',
        section: (
          <div className="dbo-flex-title-icon">
            <img src={IconHot} />
            {formatMessage('Ad Cumulative Heat')}
          </div>
        ),
        charts: [
          {
            title: formatMessage(getChartMetricLabelKey('Estimated Cumulative Heat', filter)),
            metric: 'creative_score_stat' as TChannelMetric,
            yAxisFormat: 'integer' as TYAxisFormat,
            tipsMap: adsChannelHeatScoreTips,
          },
          {
            title: formatMessage(getChartMetricLabelKey('Cumulative Impressions', filter)),
            metric: 'impression_stat' as TChannelMetric,
            yAxisFormat: 'integer' as TYAxisFormat,
            tipsMap: adsChannelImpressionTips,
          },
        ],
      },
    ],
    [formatMessage, filter],
  );

  // ==================== 状态管理 ====================
  const [chartType] = useState<TChartType>('line');
  const [channelSelection, setChannelSelection] = useState<{
    dimension_values?: string[];
    need_others?: boolean;
  } | null>({});

  // 合并 filter：页面级 + 渠道选择（只有 channelSelection 有值时才合并）
  const channelFilter = useMemo(() => {
    if (!channelSelection) return null;
    return {
      ...filter,
      ...channelSelection,
    };
  }, [filter, channelSelection]);

  // ==================== 渲染逻辑 ====================
  return (
    <div className="dbo-channel-analysis">
      {/* 标题栏 */}
      <div className="dbo-channel-analysis__header">
        <div className="dbo-channel-analysis__title-group">
          <div className="dbo-channel-analysis__title">
            {formatMessage('Active Ad Channel Analysis')}
          </div>
          <ChannelFilter
            channelOptions={channelOptions}
            onChange={setChannelSelection}
          />
        </div>
      </div>

      {/* 图表区域 */}
      {chartConfigs.map((section) => (
        <CardCommon key={section.key} title={section.section!}>
          <Row gap={20}>
            {section.charts.map((chart) => (
              <Col span={12} key={chart.metric}>
                <ChannelTrendCard
                  title={chart.title}
                  metric={chart.metric}
                  yAxisFormat={chart.yAxisFormat}
                  filter={channelFilter}
                  gameInfo={gameInfo}
                  chartType={chartType}
                  tipsMap={chart.tipsMap}
                />
              </Col>
            ))}
          </Row>
        </CardCommon>
      ))}
    </div>
  );
};

export default React.memo(ChannelAnalysis);
